import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  BookText,
  Sparkles,
  FileOutput,
  ShieldCheck,
  CalendarClock,
  Pencil,
  ArrowRight,
  CheckCircle2,
  AlertTriangle,
  History,
} from "lucide-react";
import { TenantLayout, useTenant } from "@/components/tenant/tenant-layout";
import { useAuth } from "@/lib/auth/auth-provider";
import { useRole } from "@/lib/auth/use-role";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Dot,
  EmptyState,
  Field,
  PageHeader,
  Spinner,
} from "@/components/ui/primitives";
import { listFunds } from "@/lib/api/funds";
import {
  adoptManual,
  createBrochure,
  createManual,
  draftManualWithAI,
  getDocumentByType,
  listDocumentEvents,
  listSections,
  listVersions,
  retailorManual,
  startAnnualReview,
  type ComplianceDocumentRow,
  type DocumentSectionRow,
  type DocumentType,
} from "@/lib/api/manuals";
import {
  APPLICABILITY_LABEL,
  APPLICABILITY_TONE,
  MANUAL_STATUS_LABEL,
  MANUAL_STATUS_TONE,
  SECTION_REVIEW_LABEL,
  SECTION_REVIEW_TONE,
  SECTION_SOURCE_HINT,
  SECTION_SOURCE_LABEL,
  SECTION_SOURCE_TONE,
} from "@/lib/manual-labels";
import { DOC_UI, DocPackageLink, DocSectionLink } from "@/pages/tenant/doc-nav";
import {
  countInlineGaps,
  defaultFirmProfile,
  isManualAdoptable,
  manualCompleteness,
  type FirmProfile,
  type FundStrategy,
} from "@argus/engine";

const STRATEGY_OPTIONS: { value: FundStrategy; label: string }[] = [
  { value: "buyout", label: "Buyout" },
  { value: "growth", label: "Growth equity" },
  { value: "venture", label: "Venture capital" },
  { value: "credit", label: "Private credit" },
  { value: "real_estate", label: "Real estate" },
  { value: "infrastructure", label: "Infrastructure" },
  { value: "fund_of_funds", label: "Fund-of-funds" },
  { value: "other", label: "Other" },
];

const REGISTRATION_OPTIONS: { value: FirmProfile["registrationStatus"]; label: string }[] = [
  { value: "sec_registered", label: "SEC-registered adviser" },
  { value: "exempt_reporting", label: "Exempt reporting adviser" },
  { value: "state_registered", label: "State-registered adviser" },
  { value: "unregistered", label: "Unregistered" },
];

const CUSTODY_OPTIONS: { value: FirmProfile["custodyApproach"]; label: string }[] = [
  { value: "qualified_custodian_audit", label: "Qualified custodian + annual audit" },
  { value: "surprise_exam", label: "Surprise examination" },
  { value: "no_custody", label: "No custody" },
];

const PROFILE_TOGGLES: { key: keyof FirmProfile; label: string; hint: string }[] = [
  {
    key: "hasAccessPersons",
    label: "Has access persons",
    hint: "Personal-trading reporting (Rule 204A-1)",
  },
  {
    key: "managesGovernmentEntityClients",
    label: "Government-entity investors/clients",
    hint: "Elevates pay-to-play (Rule 206(4)-5)",
  },
  {
    key: "usesPlacementAgents",
    label: "Uses placement agents",
    hint: "Pay-to-play + marketing nuance",
  },
  { key: "votesProxies", label: "Votes proxies", hint: "Proxy voting (Rule 206(4)-6)" },
  {
    key: "tradesPublicSecurities",
    label: "Trades public securities",
    hint: "Best execution + MNPI controls",
  },
  {
    key: "hasNaturalPersonClients",
    label: "Has natural-person clients",
    hint: "Privacy notice (Reg S-P)",
  },
  {
    key: "marketsToEuInvestors",
    label: "Markets to EU/EEA investors",
    hint: "AIFMD marketing + GDPR note",
  },
];

/* ---------------------------------------------------------------- intake */

function IntakeForm({
  initial,
  submitting,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  initial: FirmProfile;
  submitting: boolean;
  submitLabel: string;
  onSubmit: (p: FirmProfile) => void;
  onCancel?: () => void;
}) {
  const [p, setP] = useState<FirmProfile>(initial);
  const set = <K extends keyof FirmProfile>(key: K, value: FirmProfile[K]) =>
    setP((prev) => ({ ...prev, [key]: value }));
  const toggleStrategy = (s: FundStrategy) =>
    set(
      "fundStrategies",
      p.fundStrategies.includes(s)
        ? p.fundStrategies.filter((x) => x !== s)
        : [...p.fundStrategies, s],
    );

  const inputCls =
    "w-full rounded-md border border-neutral-800 bg-neutral-950 px-2.5 py-1.5 text-sm text-neutral-200";

  return (
    <Card className="space-y-5 p-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Firm legal name">
          <input
            value={p.firmName}
            onChange={(e) => set("firmName", e.target.value)}
            className={inputCls}
          />
        </Field>
        <Field label="Registration status">
          <select
            value={p.registrationStatus}
            onChange={(e) =>
              set("registrationStatus", e.target.value as FirmProfile["registrationStatus"])
            }
            className={inputCls}
          >
            {REGISTRATION_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Custody approach">
          <select
            value={p.custodyApproach}
            onChange={(e) =>
              set("custodyApproach", e.target.value as FirmProfile["custodyApproach"])
            }
            className={inputCls}
          >
            {CUSTODY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <Field label="Fund strategies">
        <div className="flex flex-wrap gap-2">
          {STRATEGY_OPTIONS.map((s) => {
            const on = p.fundStrategies.includes(s.value);
            return (
              <button
                key={s.value}
                type="button"
                onClick={() => toggleStrategy(s.value)}
                className={`rounded-md border px-2.5 py-1 text-xs transition ${
                  on
                    ? "border-brand-600/60 bg-brand-950/30 text-brand-200"
                    : "border-neutral-800 bg-neutral-950 text-neutral-400 hover:border-neutral-700"
                }`}
              >
                {s.label}
              </button>
            );
          })}
        </div>
      </Field>

      <div>
        <span className="mb-2 block text-[11px] font-medium uppercase tracking-wider text-neutral-500">
          Firm characteristics — these tailor which policies apply
        </span>
        <div className="grid gap-2 sm:grid-cols-2">
          {PROFILE_TOGGLES.map((t) => {
            const on = p[t.key] as boolean;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => set(t.key, !on as never)}
                className={`flex items-start gap-2.5 rounded-md border px-3 py-2 text-left transition ${
                  on
                    ? "border-brand-700/50 bg-brand-950/20"
                    : "border-neutral-800 bg-neutral-950 hover:border-neutral-700"
                }`}
              >
                <span
                  className={`mt-0.5 flex size-4 shrink-0 items-center justify-center rounded border ${
                    on ? "border-brand-500 bg-brand-600 text-white" : "border-neutral-600"
                  }`}
                >
                  {on && <CheckCircle2 className="size-3" />}
                </span>
                <span className="min-w-0">
                  <span className="block text-sm text-neutral-200">{t.label}</span>
                  <span className="block text-[11px] text-neutral-500">{t.hint}</span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex items-center gap-2 border-t border-neutral-800/70 pt-4">
        <Button
          variant="primary"
          disabled={!p.firmName.trim() || submitting}
          onClick={() => onSubmit(p)}
        >
          {submitting ? <Spinner className="size-4" /> : <Sparkles className="size-4" />}
          {submitLabel}
        </Button>
        {onCancel && (
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        )}
      </div>
    </Card>
  );
}

/* --------------------------------------------------------------- disclaimer */

function CounselNote({ noun }: { noun: string }) {
  return (
    <div className="rounded-lg border border-amber-900/50 bg-amber-950/20 px-3 py-2 text-[11px] text-amber-200/90">
      <AlertTriangle className="mr-1 inline size-3" />
      Argus prepares a tailored <strong>drafting aid</strong>, not legal advice. Every section
      requires CCO review, and the {noun} should be reviewed with counsel before adoption. Bracketed{" "}
      <code className="text-amber-300">[CCO: …]</code> markers flag firm-specific facts Argus cannot
      know.
    </div>
  );
}

/* --------------------------------------------------------------- overview */

function ManualOverview({ doc, docType }: { doc: ComplianceDocumentRow; docType: DocumentType }) {
  const tenant = useTenant();
  const { user } = useAuth();
  const qc = useQueryClient();
  const { canWrite, role } = useRole(tenant.id);
  const canEdit = canWrite || role === "reviewer";
  const ui = DOC_UI[docType];
  const [editingProfile, setEditingProfile] = useState(false);

  const { data: sections } = useQuery({
    queryKey: ["doc-sections", doc.id],
    queryFn: () => listSections(doc.id),
  });
  const { data: versions } = useQuery({
    queryKey: ["doc-versions", doc.id],
    queryFn: () => listVersions(doc.id),
  });
  const { data: events } = useQuery({
    queryKey: ["doc-events", doc.id],
    queryFn: () => listDocumentEvents(doc.id),
  });

  function invalidate() {
    void qc.invalidateQueries({ queryKey: ["doc", docType, tenant.id] });
    void qc.invalidateQueries({ queryKey: ["doc-sections", doc.id] });
    void qc.invalidateQueries({ queryKey: ["doc-versions", doc.id] });
    void qc.invalidateQueries({ queryKey: ["doc-events", doc.id] });
  }

  const draft = useMutation({
    mutationFn: () => {
      if (!user) throw new Error("Not signed in.");
      return draftManualWithAI(doc, user.id);
    },
    onSuccess: (r) => {
      invalidate();
      toast.success(
        `${r.configured ? "Claude" : "Deterministic"} draft applied to ${r.applied} section(s).`,
        {
          description: r.configured
            ? r.engine
            : "Set ANTHROPIC_API_KEY to enrich with the live model.",
        },
      );
    },
    onError: (e: Error) => toast.error("Drafting failed", { description: e.message }),
  });

  const adopt = useMutation({
    mutationFn: () => {
      if (!user) throw new Error("Not signed in.");
      return adoptManual(doc, user.id);
    },
    onSuccess: () => {
      invalidate();
      toast.success(`${ui.title} adopted — a version snapshot was frozen for your records.`);
    },
    onError: (e: Error) => toast.error("Adoption failed", { description: e.message }),
  });

  const annualReview = useMutation({
    mutationFn: () => {
      if (!user) throw new Error("Not signed in.");
      return startAnnualReview(doc, user.id);
    },
    onSuccess: (r) => {
      invalidate();
      toast.success("Annual review started.", {
        description: r.linkedTaskId
          ? "Linked to your annual compliance review on the calendar."
          : "Every applicable section was reset for re-review.",
      });
    },
    onError: (e: Error) => toast.error("Could not start review", { description: e.message }),
  });

  const retailor = useMutation({
    mutationFn: (p: FirmProfile) => {
      if (!user) throw new Error("Not signed in.");
      return retailorManual(doc, p, user.id);
    },
    onSuccess: () => {
      invalidate();
      setEditingProfile(false);
      toast.success("Re-tailored to the updated firm profile.");
    },
    onError: (e: Error) => toast.error("Re-tailor failed", { description: e.message }),
  });

  if (editingProfile) {
    return (
      <>
        <PageHeader
          title="Firm profile"
          subtitle={`Update the inputs that tailor your ${ui.noun}.`}
        />
        <IntakeForm
          initial={doc.firm_profile}
          submitting={retailor.isPending}
          submitLabel={`Re-tailor ${ui.noun}`}
          onSubmit={(p) => retailor.mutate(p)}
          onCancel={() => setEditingProfile(false)}
        />
      </>
    );
  }

  const rows = sections ?? [];
  const stateRows = rows.map((s) => ({
    applicability: s.applicability,
    source: s.source,
    body: s.body,
    reviewStatus: s.review_status,
  }));
  const completeness = manualCompleteness(stateRows);
  const adoptable = isManualAdoptable(stateRows);
  const included = rows.filter((s) => s.applicability !== "not_applicable");
  const excluded = rows.filter((s) => s.applicability === "not_applicable");

  return (
    <>
      <PageHeader
        title={ui.title}
        subtitle={
          <span className="flex flex-wrap items-center gap-1.5">
            <Badge tone={MANUAL_STATUS_TONE[doc.status]}>{MANUAL_STATUS_LABEL[doc.status]}</Badge>
            {doc.current_version > 0 && (
              <span className="text-neutral-500">v{doc.current_version}</span>
            )}
            {doc.effective_date && (
              <span className="text-neutral-600">· effective {doc.effective_date}</span>
            )}
            {doc.engine_version && (
              <span className="text-neutral-600">· model {doc.engine_version}</span>
            )}
          </span>
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {canEdit && (
              <Button
                variant="ghost"
                onClick={() => setEditingProfile(true)}
                title="Edit firm profile"
              >
                <Pencil className="size-4" /> Profile
              </Button>
            )}
            {canEdit && (
              <Button variant="secondary" disabled={draft.isPending} onClick={() => draft.mutate()}>
                {draft.isPending ? <Spinner className="size-4" /> : <Sparkles className="size-4" />}
                Draft with AI
              </Button>
            )}
            <DocPackageLink docType={docType} slug={tenant.slug}>
              <Button variant="primary">
                <FileOutput className="size-4" /> {ui.packageLabel}
              </Button>
            </DocPackageLink>
          </div>
        }
      />

      <CounselNote noun={ui.noun} />

      {/* Completeness + lifecycle */}
      <Card className="p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-6 text-sm">
            <Stat label="Applicable" value={completeness.applicable} />
            <Stat label="Drafted" value={`${completeness.drafted}/${completeness.applicable}`} />
            <Stat label="Reviewed" value={`${completeness.reviewedPct}%`} />
            <Stat
              label="Flagged"
              value={completeness.flagged}
              tone={completeness.flagged > 0 ? "risk" : "clear"}
            />
            <Stat
              label="CCO inputs"
              value={completeness.inlineGaps}
              tone={completeness.inlineGaps > 0 ? "risk" : "clear"}
            />
          </div>
          <div className="flex items-center gap-2">
            <div className="h-1.5 w-40 overflow-hidden rounded-full bg-neutral-800">
              <div
                className="bg-brand-500 h-full rounded-full transition-all"
                style={{ width: `${completeness.reviewedPct}%` }}
              />
            </div>
            <span className="text-[11px] tabular-nums text-neutral-500">
              {completeness.reviewedPct}% reviewed
            </span>
          </div>
        </div>

        {canEdit && (
          <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-neutral-800/70 pt-3">
            <Button
              size="sm"
              variant="success"
              disabled={!adoptable || adopt.isPending}
              onClick={() => adopt.mutate()}
              title={
                adoptable
                  ? "Freeze a version and mark adopted"
                  : "Every applicable section must be reviewed first"
              }
            >
              <ShieldCheck className="size-3.5" />
              {doc.current_version > 0 ? "Re-adopt (freeze version)" : `Adopt ${ui.noun}`}
            </Button>
            {doc.status === "adopted" && (
              <Button
                size="sm"
                variant="secondary"
                disabled={annualReview.isPending}
                onClick={() => annualReview.mutate()}
                title="Start the Rule 206(4)-7 annual review"
              >
                <CalendarClock className="size-3.5" /> Start annual review
              </Button>
            )}
            {!adoptable && (
              <span className="inline-flex items-center gap-1 text-[11px] text-amber-300/80">
                <AlertTriangle className="size-3" />{" "}
                {completeness.gaps + (completeness.applicable - completeness.reviewed)} section(s)
                need review before adoption
              </span>
            )}
          </div>
        )}
      </Card>

      <div className="grid gap-4 lg:grid-cols-[1fr_300px]">
        <div className="space-y-4">
          <Card>
            <CardHeader
              title="Sections"
              action={
                <span className="text-[11px] tabular-nums text-neutral-500">
                  {included.length} included
                  {excluded.length > 0 ? ` · ${excluded.length} N/A` : ""}
                </span>
              }
            />
            <div className="divide-y divide-neutral-800/60 border-t border-neutral-800/70">
              {included.map((s) => (
                <SectionRow key={s.id} section={s} slug={tenant.slug} docType={docType} />
              ))}
            </div>
            {excluded.length > 0 && (
              <div className="border-t border-neutral-800/70">
                <div className="bg-neutral-900/40 px-4 py-1.5 text-[11px] font-medium uppercase tracking-wide text-neutral-500">
                  Not applicable to this firm
                </div>
                <div className="divide-y divide-neutral-800/60">
                  {excluded.map((s) => (
                    <SectionRow key={s.id} section={s} slug={tenant.slug} docType={docType} />
                  ))}
                </div>
              </div>
            )}
          </Card>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader title="Provenance" />
            <div className="space-y-2 px-4 pb-4 text-[11px]">
              {(["auto", "ai", "manual"] as const).map((s) => (
                <div key={s} className="flex items-start gap-2">
                  <Badge tone={SECTION_SOURCE_TONE[s]}>{SECTION_SOURCE_LABEL[s]}</Badge>
                  <span className="text-neutral-500">{SECTION_SOURCE_HINT[s]}</span>
                </div>
              ))}
            </div>
          </Card>

          <Card>
            <CardHeader
              title="Version history"
              action={<History className="size-3.5 text-neutral-600" />}
            />
            <div className="space-y-2.5 px-4 pb-4 text-xs">
              {(versions ?? []).length === 0 ? (
                <p className="text-neutral-500">No adopted versions yet.</p>
              ) : (
                (versions ?? []).map((v) => (
                  <div key={v.id} className="flex gap-2">
                    <Badge tone="clear">v{v.version_number}</Badge>
                    <div className="min-w-0">
                      <div className="text-neutral-300">{v.change_summary}</div>
                      <div className="text-neutral-600">
                        effective {v.effective_date ?? "—"} ·{" "}
                        {new Date(v.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>

          <Card>
            <CardHeader title="Activity" />
            <div className="max-h-72 space-y-2.5 overflow-y-auto px-4 pb-4 text-xs">
              {(events ?? []).length === 0 ? (
                <p className="text-neutral-500">No activity yet.</p>
              ) : (
                (events ?? []).map((ev) => (
                  <div key={ev.id} className="flex gap-2">
                    <Dot tone="neutral" className="mt-1.5" />
                    <div className="min-w-0">
                      <div className="text-neutral-300">{ev.event_type.replace(/_/g, " ")}</div>
                      {ev.note && <div className="text-neutral-500">{ev.note}</div>}
                      <div className="text-neutral-600">
                        {new Date(ev.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}

function SectionRow({
  section,
  slug,
  docType,
}: {
  section: DocumentSectionRow;
  slug: string;
  docType: DocumentType;
}) {
  const naApplicable = section.applicability === "not_applicable";
  const inline = countInlineGaps(section.body);
  return (
    <DocSectionLink
      docType={docType}
      slug={slug}
      sectionKey={section.section_key}
      className="flex items-center gap-3 px-4 py-3 transition hover:bg-neutral-900/60"
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span
            className={`text-sm font-medium ${naApplicable ? "text-neutral-500" : "text-neutral-100"}`}
          >
            {section.heading}
          </span>
          {section.reg_ref && (
            <span className="text-[10px] uppercase tracking-wide text-neutral-600">
              {section.reg_ref}
            </span>
          )}
        </div>
      </div>
      <Badge tone={APPLICABILITY_TONE[section.applicability]}>
        {APPLICABILITY_LABEL[section.applicability]}
      </Badge>
      {!naApplicable && (
        <>
          <Badge tone={SECTION_SOURCE_TONE[section.source]}>
            {SECTION_SOURCE_LABEL[section.source]}
          </Badge>
          {inline > 0 && (
            <Badge tone="risk">
              <Dot tone="risk" /> {inline} input{inline > 1 ? "s" : ""}
            </Badge>
          )}
          <Badge tone={SECTION_REVIEW_TONE[section.review_status]}>
            {SECTION_REVIEW_LABEL[section.review_status]}
          </Badge>
        </>
      )}
      <ArrowRight className="size-4 text-neutral-600" />
    </DocSectionLink>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string | number;
  tone?: "risk" | "clear";
}) {
  const color =
    tone === "risk" ? "text-amber-300" : tone === "clear" ? "text-emerald-300" : "text-neutral-100";
  return (
    <div>
      <div className={`text-base font-semibold tabular-nums ${color}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-neutral-600">{label}</div>
    </div>
  );
}

/* ----------------------------------------------------------------- page */

function ManualInner({ docType }: { docType: DocumentType }) {
  const tenant = useTenant();
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { canWrite, role } = useRole(tenant.id);
  const canEdit = canWrite || role === "reviewer";
  const ui = DOC_UI[docType];

  const { data: doc, isLoading } = useQuery({
    queryKey: ["doc", docType, tenant.id],
    queryFn: () => getDocumentByType(tenant.id, docType),
  });
  const { data: funds } = useQuery({
    queryKey: ["funds", tenant.id],
    queryFn: () => listFunds(tenant.id),
  });

  const create = useMutation({
    mutationFn: (p: FirmProfile) => {
      if (!user) throw new Error("Not signed in.");
      const args = { tenantId: tenant.id, profile: p, userId: user.id };
      return docType === "adv_2a_brochure" ? createBrochure(args) : createManual(args);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["doc", docType, tenant.id] });
      toast.success(`${ui.title} created and tailored to your firm.`);
    },
    onError: (e: Error) => toast.error(`Could not create ${ui.noun}`, { description: e.message }),
  });

  if (isLoading) return <p className="text-sm text-neutral-500">Loading {ui.noun}…</p>;

  if (doc) return <ManualOverview doc={doc} docType={docType} />;

  if (!canEdit) {
    return (
      <>
        <PageHeader title={ui.title} />
        <EmptyState
          icon={<BookText className="size-8" />}
          title={ui.emptyTitle}
          description={ui.emptyDescription}
        />
      </>
    );
  }

  const suggested: FirmProfile = (() => {
    const base = defaultFirmProfile(tenant.name);
    const strategies = [
      ...new Set((funds ?? []).map((f) => f.strategy).filter(Boolean)),
    ] as FundStrategy[];
    return { ...base, fundStrategies: strategies.length > 0 ? strategies : base.fundStrategies };
  })();

  return (
    <>
      <PageHeader title={ui.buildTitle} subtitle={ui.buildSubtitle} />
      <CounselNote noun={ui.noun} />
      <IntakeForm
        initial={suggested}
        submitting={create.isPending}
        submitLabel={ui.createLabel}
        onSubmit={(p) =>
          create.mutate(p, {
            onSuccess: () => {
              void navigate({
                to: docType === "adv_2a_brochure" ? "/t/$slug/brochure" : "/t/$slug/manual",
                params: { slug: tenant.slug },
              });
            },
          })
        }
      />
    </>
  );
}

export function ManualPage({ docType = "compliance_manual" }: { docType?: DocumentType }) {
  return (
    <TenantLayout>
      <ManualInner docType={docType} />
    </TenantLayout>
  );
}
